import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { Menu, Popover, Popconfirm, Dropdown } from 'antd';
import classnames from 'classnames';
import _ from 'lodash';

import { getAddress } from '@laigu/utils';

import { copyConvToClipboard } from '@laigu/utils/clipboard';

import Avatar from '../../avatar';
import MyIcon from '../../my-icon';
import useKeydown from '../../../hooks/use-keydown';
import useUpgrade from '../../../hooks/use-upgrade';

import style from './index.less';
import { IdParams } from '../typings';

import { enterpriseStore, useCustomerTag } from '@laigu/store';

import BlockApply from '../block-apply';
import { SystemTag } from '@/component';
import TagSelect from '../../tag-select';

const { useEnterpriseConfig } = enterpriseStore;

const blacklistText = {
  title: '更多操作',
  addTitle: '加入黑名单',
  remoteTitle: '移除黑名单',
  ok: '确认',
  cancel: '取消'
};

type AvatarTagProps = {
  visitInfo: VisitInfo;
  tagList: Array<number>;
  inBlackList?: boolean;
  onAddInBlacklist?: (param: IdParams) => void;
  onRemoveInBlacklist?: (param: IdParams) => void;
  idParams: IdParams;
  name: string;
  onAddClientTag?: (param: IdParams & { tagId: number }) => void | Promise<any>;
  onDeleteClientTag?: (param: IdParams & { tagId: number }) => void | Promise<any>;
  disabledKeydown?: boolean;
};

type ReactEvent = React.KeyboardEvent<HTMLElement>;

const AvatarTag = ({
  visitInfo,
  tagList,
  inBlackList = false,
  onAddInBlacklist,
  onRemoveInBlacklist,
  idParams,
  name,
  onAddClientTag,
  onDeleteClientTag,
  disabledKeydown
}: AvatarTagProps): JSX.Element => {
  const { country, province, city } = visitInfo;
  const isInBlacklist = inBlackList;
  const { customerTagTree, customerTagMap } = useCustomerTag();
  const { bindFunctionAndPerm, checkFunctionAndPerm } = useUpgrade();
  const [visible, toggleVisible] = useState(false);
  const { enterpriseConfig, handleUpdateEnterpriseConfig } = useEnterpriseConfig();
  const { blacklist_application_config } = enterpriseConfig;
  const [applyModalVisible, setApplyModalVisible] = useState(false);

  // 本地标签状态：完全由前端管理
  const [localTagList, setLocalTagList] = useState(tagList || []);
  const prevTrackIdRef = useRef(idParams.trackId);
  const prevTagListRef = useRef<number[]>(tagList || []);

  // 当 trackId 变化或 tagList 内容真正变化时更新
  useEffect(() => {
    const isTrackIdChanged = prevTrackIdRef.current !== idParams.trackId;

    if (isTrackIdChanged) {
      // 切换顾客，强制更新
      setLocalTagList(tagList || []);
      prevTrackIdRef.current = idParams.trackId;
      prevTagListRef.current = tagList || [];
    } else {
      // trackId 未变，检查 tagList 内容是否真的变化
      // 避免因父组件其他状态变化导致 tagList 引用变化但内容相同时的重复渲染
      const currentTagList = tagList || [];
      const prevTagList = prevTagListRef.current;

      const hasContentChanged =
        currentTagList.length !== prevTagList.length || currentTagList.some((tag, index) => tag !== prevTagList[index]);

      if (hasContentChanged) {
        setLocalTagList(currentTagList);
        prevTagListRef.current = currentTagList;
      }
    }
  }, [idParams.trackId, tagList]);

  const address = getAddress({ country, province, city });

  const selectedTags = (localTagList || []).map(id => customerTagMap[id])?.filter(Boolean);

  const notEmptyTag = selectedTags?.length > 0;

  const menu = (
    <Menu
      items={[
        {
          key: 'blacklist',
          label: isInBlacklist ? (
            <Popconfirm
              placement='leftBottom'
              title={
                <>
                  <span style={{ fontSize: '16px', fontWeight: '500', color: '#1D2754' }}>
                    {blacklistText.remoteTitle}
                  </span>
                  <div style={{ paddingTop: '10px', color: '#6F7592' }}>
                    {'确定移除黑名单？移除后该访客可正常访问'}
                  </div>
                </>
              }
              onConfirm={() => {
                if (onRemoveInBlacklist && checkFunctionAndPerm('visit_blacklist', 'add_del_client_blacklist')) {
                  onRemoveInBlacklist(idParams);
                }
              }}
              okText={blacklistText.ok}
              cancelText={blacklistText.cancel}
            >
              <span className={style.handle_black}>{blacklistText.remoteTitle}</span>
            </Popconfirm>
          ) : (
            blacklist_application_config?.status == 'open' ? (
              <span
                className={style.handle_black}
                onClick={() => {
                  if (onAddInBlacklist && checkFunctionAndPerm('visit_blacklist', 'add_del_client_blacklist')) {
                    setApplyModalVisible(true);
                  }
                }}
              >
                {' '}
                {blacklistText.addTitle}
              </span>
            ) : (
              <Popconfirm
                placement='leftBottom'
                title={blacklistText.addTitle}
                onConfirm={() => {
                  if (onAddInBlacklist && checkFunctionAndPerm('visit_blacklist', 'add_del_client_blacklist')) {
                    onAddInBlacklist(idParams);
                  }
                }}
                okText={blacklistText.ok}
                cancelText={blacklistText.cancel}
              >
                <span className={style.handle_black}>{blacklistText.addTitle}</span>
              </Popconfirm>
            )
          )
        },
        {
          key: 'copyConv',
          label: '复制对话内容',
          onClick: () => copyConvToClipboard()
        },
        {
          key: 'copyMsg',
          label: '复制对话消息',
          onClick: () => copyConvToClipboard(true)
        }
      ]}
    />
  );

  const onChange = useCallback(
    (ids: number[]) => {
      const currentTagList = localTagList || [];

      // 乐观更新：立即更新本地状态
      setLocalTagList(ids);

      if (ids.length > currentTagList.length) {
        // 添加标签
        const addId = ids.find(id => !currentTagList.includes(id));
        if (addId && onAddClientTag) {
          try {
            // 捕获 Promise 失败，进行回滚
            const result = onAddClientTag({ ...idParams, tagId: addId });
            // 检查是否返回 Promise
            if (result && typeof (result as any)?.catch === 'function') {
              (result as Promise<any>).catch(() => {
                // 请求失败，回滚到之前的状态
                setLocalTagList(currentTagList);
              });
            }
          } catch (error) {
            // 同步错误也要回滚
            setLocalTagList(currentTagList);
          }
        }
      } else {
        // 删除标签
        const removeId = currentTagList.find(id => !ids.includes(id));
        if (removeId && onDeleteClientTag) {
          try {
            // 捕获 Promise 失败，进行回滚
            const result = onDeleteClientTag({ ...idParams, tagId: removeId });
            // 检查是否返回 Promise
            if (result && typeof (result as any)?.catch === 'function') {
              (result as Promise<any>).catch(() => {
                // 请求失败，回滚到之前的状态
                setLocalTagList(currentTagList);
              });
            }
          } catch (error) {
            // 同步错误也要回滚
            setLocalTagList(currentTagList);
          }
        }
      }
    },
    [localTagList, idParams, onAddClientTag, onDeleteClientTag]
  );

  const renderTag = (): JSX.Element => {
    return <TagSelect alwaysOpen multiple data={customerTagTree} value={localTagList} onChange={onChange} />;
  };

  const handleTagClose = useCallback(
    (tagId: number) => {
      // 保存当前状态，用于失败回滚
      const previousTagList = localTagList || [];

      // 乐观更新：立即更新本地状态
      setLocalTagList(prev => prev.filter(id => id !== tagId));

      // 发送删除请求，失败时回滚
      if (onDeleteClientTag) {
        try {
          const result = onDeleteClientTag({ ...idParams, tagId });
          // 检查是否返回 Promise
          if (result && typeof (result as any)?.catch === 'function') {
            (result as Promise<any>).catch(() => {
              // 请求失败，回滚到之前的状态
              setLocalTagList(previousTagList);
            });
          }
        } catch (error) {
          // 同步错误也要回滚
          setLocalTagList(previousTagList);
        }
      }
    },
    [localTagList, idParams, onDeleteClientTag]
  );

  useKeydown(
    (e: ReactEvent) => {
      if (e.keyCode === 84 && !disabledKeydown) {
        toggleVisible(true);
      }
    },
    [disabledKeydown]
  );

  const onAddClick = useCallback(() => {
    toggleVisible(true);
  }, []);

  const onClose = useCallback(
    e => {
      if (visible && !e) {
        toggleVisible(false);
      }
    },
    [visible]
  );

  // 申请拉黑成功成功回掉
  const onApplySuccess = () => {
    setApplyModalVisible(false);
  };

  return (
    <div className={style.box}>
      <div className={style['avatar-box']}>
        <Avatar visitInfo={visitInfo} className={style['avatar-img']} size={48} />
        <p title={classnames({ [address]: address }, name)} className={style['avatar-name']}>
          {classnames({ [address]: address }, name)}
        </p>
        {onAddInBlacklist && onRemoveInBlacklist && (
          <div className={style['operation-box']}>
            <Dropdown dropdownRender={() => menu} trigger={['click']}>
              <MoreOutlined title={blacklistText.title} />
            </Dropdown>
          </div>
        )}
      </div>
      <div className={style['name-tag-box']}>
        <div>
          {selectedTags.map(data => (
            <SystemTag key={data.id} data={data} closabel onClose={() => handleTagClose(data.id)} />
          ))}
          {notEmptyTag && !!onAddClientTag && (
            <Popover
              content={renderTag()}
              trigger='click'
              open={visible}
              placement='bottom'
              onOpenChange={onClose}
            >
              <div
                className={style['add-tag-icon']}
                onClick={bindFunctionAndPerm(onAddClick, 'client_tag', 'add_del_client_tag')}
              >
                <MyIcon type='ecoboost-iconfontic_add_' title={'顾客标签'} />
              </div>
            </Popover>
          )}
          {!notEmptyTag && (
            <Popover
              content={renderTag()}
              trigger='click'
              open={visible}
              placement='bottom'
              onOpenChange={onClose}
            >
              <span
                className={style['add-tag']}
                onClick={bindFunctionAndPerm(onAddClick, 'client_tag', 'add_del_client_tag')}
              >
                <PlusOutlined />
                <span>{'顾客标签'}</span>
              </span>
            </Popover>
          )}
        </div>
      </div>
      <BlockApply
        idParams={idParams}
        visible={applyModalVisible}
        handleCancel={() => setApplyModalVisible(false)}
        onApplySuccess={onApplySuccess}
      />
    </div>
  );
};

export default AvatarTag;
